import { useState, useEffect, useCallback } from "react";
import { useAuth, apiClient } from "../App";
import DashboardLayout from "../components/DashboardLayout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { toast } from "sonner";
import {
  FileText, Plus, DollarSign, Clock, CheckCircle2, AlertCircle,
  XCircle, Search, Eye, CreditCard, Building2, Calendar, BellRing, Send, RefreshCw
} from "lucide-react";
import { useLanguage } from "../i18n/LanguageContext";

const statusColors = {
  draft: "bg-slate-100 text-slate-600",
  pending: "bg-amber-100 text-amber-700",
  paid: "bg-emerald-100 text-emerald-700",
  overdue: "bg-red-100 text-red-700",
  cancelled: "bg-slate-100 text-slate-400",
};

const statusIcons = {
  draft: FileText,
  pending: Clock,
  paid: CheckCircle2,
  overdue: AlertCircle,
  cancelled: XCircle,
};

export default function InvoiceManagement() {
  const { user } = useAuth();
  const { t } = useLanguage();
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [filterStatus, setFilterStatus] = useState("all");
  const [searchTerm, setSearchTerm] = useState("");

  // Create invoice dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [slaContracts, setSlaContracts] = useState([]);
  const [createForm, setCreateForm] = useState({
    client_id: "", sla_contract_id: "", billing_type: "monthly",
    billing_period_start: "", billing_period_end: "", due_date: "",
    description: "", quantity: "1", unit_price: "", currency: "BSD",
    payment_method: "bank_transfer", payment_processor: "manual", notes: "",
  });

  // Mark as paid dialog
  const [markPaidOpen, setMarkPaidOpen] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [markPaidForm, setMarkPaidForm] = useState({
    reference_number: "", bank_name: "", date_received: "", notes: "",
  });

  // Detail dialog
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailInvoice, setDetailInvoice] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const [invRes, statsRes] = await Promise.all([
        apiClient.get("/invoices"),
        apiClient.get("/invoices/stats/summary"),
      ]);
      setInvoices(invRes.data);
      setStats(statsRes.data);
      // Load clients for create form
      const usersRes = await apiClient.get("/users");
      setClients(usersRes.data.filter((u) => u.role === "client"));
      const slaRes = await apiClient.get("/sla-contracts");
      setSlaContracts(slaRes.data);
    } catch {
      toast.error("Failed to load invoice data");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleCreate = async () => {
    if (!createForm.client_id || !createForm.due_date || !createForm.unit_price) {
      toast.error("Please fill required fields"); return;
    }
    try {
      const total = parseFloat(createForm.quantity) * parseFloat(createForm.unit_price);
      const res = await apiClient.post("/invoices", {
        client_id: createForm.client_id,
        sla_contract_id: createForm.sla_contract_id || null,
        billing_type: createForm.billing_type,
        billing_period_start: createForm.billing_period_start || null,
        billing_period_end: createForm.billing_period_end || null,
        due_date: createForm.due_date,
        items: [{ description: createForm.description || "Service billing", quantity: parseFloat(createForm.quantity), unit_price: parseFloat(createForm.unit_price), total }],
        currency: createForm.currency,
        payment_method: createForm.payment_method,
        payment_processor: createForm.payment_processor,
        notes: createForm.notes,
      });
      if (res.data?.email_delivery?.sent) {
        toast.success(`Invoice ${res.data.invoice_number} created and emailed to ${res.data.client_email}`);
      } else {
        const reason = res.data?.email_delivery?.error || "unknown email error";
        toast.warning(`Invoice ${res.data?.invoice_number || ""} was created, but the email was not sent: ${reason}`);
      }
      setCreateOpen(false);
      setCreateForm({ client_id: "", sla_contract_id: "", billing_type: "monthly", billing_period_start: "", billing_period_end: "", due_date: "", description: "", quantity: "1", unit_price: "", currency: "BSD", payment_method: "bank_transfer", payment_processor: "manual", notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create invoice");
    }
  };

  const handleMarkPaid = async () => {
    if (!markPaidForm.reference_number || !markPaidForm.bank_name || !markPaidForm.date_received) {
      toast.error("Please fill all required fields"); return;
    }
    try {
      await apiClient.post(`/invoices/${selectedInvoice.id}/mark-paid`, markPaidForm);
      toast.success("Invoice marked as paid");
      setMarkPaidOpen(false);
      setMarkPaidForm({ reference_number: "", bank_name: "", date_received: "", notes: "" });
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update invoice");
    }
  };

  const handleReconcileStripe = async (inv) => {
    if (!window.confirm(`Query Stripe for ${inv.invoice_number} and mark paid if confirmed?`)) return;
    try {
      const res = await apiClient.post(`/invoices/${inv.id}/reconcile-stripe`);
      if (res.data.reconciled) {
        toast.success(`${inv.invoice_number} reconciled and marked paid`);
        fetchData();
      } else if (res.data.already_paid) {
        toast.info("Already paid");
        fetchData();
      } else {
        toast.warning(`Stripe reports: ${res.data.stripe_status || "not paid"}`);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Reconcile failed");
    }
  };


  const handleCancel = async (id) => {
    if (!window.confirm("Cancel this invoice?")) return;
    try {
      await apiClient.post(`/invoices/${id}/cancel`);
      toast.success("Invoice cancelled");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to cancel");
    }
  };

  const filtered = invoices.filter((inv) => {
    if (filterStatus !== "all" && inv.status !== filterStatus) return false;
    if (searchTerm && !inv.client_name.toLowerCase().includes(searchTerm.toLowerCase()) && !inv.invoice_number.toLowerCase().includes(searchTerm.toLowerCase())) return false;
    return true;
  });

  if (loading) return <DashboardLayout><div className="flex items-center justify-center h-64"><div className="animate-spin h-8 w-8 border-4 border-sag-navy border-t-transparent rounded-full" /></div></DashboardLayout>;

  return (
    <DashboardLayout>
      <div className="space-y-6" data-testid="invoice-management">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Invoice Management</h1>
            <p className="text-slate-500 mt-1">Create, track, and manage client invoices</p>
          </div>
          <Button className="bg-sag-navy hover:bg-sag-navy-800" onClick={() => setCreateOpen(true)} data-testid="create-invoice-btn">
            <Plus className="h-4 w-4 mr-2" /> New Invoice
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Total Invoiced", value: stats.total_invoiced, icon: FileText, color: "bg-sag-navy/10 text-sag-navy" },
            { label: "Paid", value: stats.total_paid, icon: CheckCircle2, color: "bg-emerald-100 text-emerald-600" },
            { label: "Pending", value: stats.total_pending, icon: Clock, color: "bg-amber-100 text-amber-600" },
            { label: "Overdue", value: stats.total_overdue, icon: AlertCircle, color: "bg-red-100 text-red-600" },
          ].map((stat) => (
            <Card key={stat.label} className="sag-card">
              <CardContent className="p-4 flex items-center gap-3">
                <div className={`p-2.5 rounded-xl ${stat.color}`}><stat.icon className="h-5 w-5" /></div>
                <div>
                  <p className="text-xl font-bold font-heading">${(stat.value || 0).toLocaleString()}</p>
                  <p className="text-xs text-slate-500">{stat.label}</p>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Filters */}
        <div className="flex items-center gap-3">
          <div className="relative flex-1 max-w-sm">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
            <Input placeholder="Search by client or invoice #..." value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)} className="pl-9" data-testid="invoice-search" />
          </div>
          <Select value={filterStatus} onValueChange={setFilterStatus}>
            <SelectTrigger className="w-[140px]" data-testid="invoice-filter-status"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Status</SelectItem>
              <SelectItem value="pending">Pending</SelectItem>
              <SelectItem value="paid">Paid</SelectItem>
              <SelectItem value="overdue">Overdue</SelectItem>
              <SelectItem value="cancelled">Cancelled</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {/* Invoice Table */}
        <Card className="sag-card">
          <CardContent className="p-0">
            {filtered.length === 0 ? (
              <div className="p-12 text-center text-slate-500">
                <FileText className="h-12 w-12 mx-auto mb-3 text-slate-300" />
                <p>No invoices found</p>
              </div>
            ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Invoice #</TableHead>
                    <TableHead>Client</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Due Date</TableHead>
                    <TableHead>Method</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filtered.map((inv) => {
                    const StatusIcon = statusIcons[inv.status] || FileText;
                    return (
                      <TableRow key={inv.id} data-testid={`invoice-row-${inv.id}`}>
                        <TableCell className="font-mono text-sm font-medium">{inv.invoice_number}</TableCell>
                        <TableCell>
                          <div><p className="font-medium text-sm">{inv.client_name}</p><p className="text-xs text-slate-500">{inv.client_email}</p></div>
                        </TableCell>
                        <TableCell><Badge variant="outline" className="capitalize text-xs">{inv.billing_type}</Badge></TableCell>
                        <TableCell className="font-semibold">${inv.total.toLocaleString()} <span className="text-xs text-slate-400">{inv.currency}</span></TableCell>
                        <TableCell className="text-sm">{new Date(inv.due_date).toLocaleDateString()}</TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            {inv.payment_method === "bank_transfer" ? <Building2 className="h-3.5 w-3.5 text-slate-500" /> : <CreditCard className="h-3.5 w-3.5 text-slate-500" />}
                            <span className="text-xs capitalize">{(inv.payment_method || "—").replace("_", " ")}</span>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={statusColors[inv.status]}>
                            <StatusIcon className="h-3 w-3 mr-1" />
                            <span className="capitalize">{inv.status}</span>
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="sm" onClick={() => { setDetailInvoice(inv); setDetailOpen(true); }} data-testid={`view-invoice-${inv.id}`}>
                              <Eye className="h-3.5 w-3.5" />
                            </Button>
                            {(inv.status === "pending" || inv.status === "overdue") && (
                              <>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-blue-600 hover:text-blue-700"
                                  title="Send Reminder / Overdue Notice"
                                  onClick={async () => {
                                    try {
                                      const res = await apiClient.post(`/invoices/${inv.id}/send-reminder`);
                                      toast.success(res.data.message);
                                      fetchData();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || "Failed to send reminder");
                                    }
                                  }}
                                  data-testid={`send-reminder-${inv.id}`}
                                >
                                  <BellRing className="h-3.5 w-3.5" />
                                </Button>
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  className="text-indigo-600 hover:text-indigo-700"
                                  title="Resend Invoice"
                                  onClick={async () => {
                                    try {
                                      const res = await apiClient.post(`/invoices/${inv.id}/send`);
                                      toast.success(res.data.message);
                                      fetchData();
                                    } catch (err) {
                                      toast.error(err.response?.data?.detail || "Failed to resend invoice");
                                    }
                                  }}
                                  data-testid={`resend-invoice-${inv.id}`}
                                >
                                  <Send className="h-3.5 w-3.5" />
                                </Button>
                                <Button variant="ghost" size="sm" className="text-emerald-600 hover:text-emerald-700" onClick={() => { setSelectedInvoice(inv); setMarkPaidOpen(true); }} data-testid={`mark-paid-${inv.id}`}>
                                  <CheckCircle2 className="h-3.5 w-3.5" />
                                </Button>
                                {inv.payment_processor === "stripe" && (
                                  <Button variant="ghost" size="sm" className="text-blue-600 hover:text-blue-700" onClick={() => handleReconcileStripe(inv)} title="Reconcile with Stripe" data-testid={`reconcile-stripe-${inv.id}`}>
                                    <RefreshCw className="h-3.5 w-3.5" />
                                  </Button>
                                )}
                                <Button variant="ghost" size="sm" className="text-red-500 hover:text-red-600" onClick={() => handleCancel(inv.id)}>
                                  <XCircle className="h-3.5 w-3.5" />
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            )}
          </CardContent>
        </Card>

        {/* Create Invoice Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Create Invoice</DialogTitle></DialogHeader>
            <div className="space-y-4">
              <div className="space-y-2">
                <Label>Client *</Label>
                <Select value={createForm.client_id} onValueChange={(v) => setCreateForm({ ...createForm, client_id: v })}>
                  <SelectTrigger data-testid="create-inv-client"><SelectValue placeholder="Select client" /></SelectTrigger>
                  <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.full_name} ({c.email})</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>SLA Contract (optional)</Label>
                <Select value={createForm.sla_contract_id} onValueChange={(v) => setCreateForm({ ...createForm, sla_contract_id: v })}>
                  <SelectTrigger><SelectValue placeholder="Link to SLA contract" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {slaContracts.filter((s) => s.client_id === createForm.client_id).map((s) => <SelectItem key={s.id} value={s.id}>{s.title}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Billing Type</Label>
                  <Select value={createForm.billing_type} onValueChange={(v) => setCreateForm({ ...createForm, billing_type: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="quarterly">Quarterly</SelectItem>
                      <SelectItem value="annual">Annual</SelectItem>
                      <SelectItem value="one_time">One-Time</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Currency</Label>
                  <Select value={createForm.currency} onValueChange={(v) => setCreateForm({ ...createForm, currency: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="BSD">BSD</SelectItem>
                      <SelectItem value="USD">USD</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Period Start</Label><Input type="date" value={createForm.billing_period_start} onChange={(e) => setCreateForm({ ...createForm, billing_period_start: e.target.value })} /></div>
                <div className="space-y-2"><Label>Period End</Label><Input type="date" value={createForm.billing_period_end} onChange={(e) => setCreateForm({ ...createForm, billing_period_end: e.target.value })} /></div>
              </div>
              <div className="space-y-2"><Label>Due Date *</Label><Input type="date" value={createForm.due_date} onChange={(e) => setCreateForm({ ...createForm, due_date: e.target.value })} data-testid="create-inv-due-date" /></div>
              <div className="space-y-2"><Label>Description</Label><Input value={createForm.description} onChange={(e) => setCreateForm({ ...createForm, description: e.target.value })} placeholder="Service billing description" data-testid="create-inv-description" /></div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2"><Label>Quantity</Label><Input type="number" min="1" value={createForm.quantity} onChange={(e) => setCreateForm({ ...createForm, quantity: e.target.value })} /></div>
                <div className="space-y-2"><Label>Unit Price *</Label><Input type="number" min="0" step="0.01" value={createForm.unit_price} onChange={(e) => setCreateForm({ ...createForm, unit_price: e.target.value })} placeholder="0.00" data-testid="create-inv-price" /></div>
              </div>
              {createForm.unit_price && (
                <div className="bg-sag-navy/5 rounded-lg p-3 text-center">
                  <p className="text-xs text-slate-500 uppercase">Total</p>
                  <p className="text-2xl font-bold text-sag-navy">${(parseFloat(createForm.quantity || 1) * parseFloat(createForm.unit_price || 0)).toFixed(2)} {createForm.currency}</p>
                </div>
              )}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Payment Method</Label>
                  <Select value={createForm.payment_method} onValueChange={(v) => setCreateForm({ ...createForm, payment_method: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="card">Credit Card</SelectItem>
                      <SelectItem value="bank_transfer">Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Processor</Label>
                  <Select value={createForm.payment_processor} onValueChange={(v) => setCreateForm({ ...createForm, payment_processor: v })}>
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="fiserv">Scotiabank</SelectItem>
                      <SelectItem value="stripe">Stripe</SelectItem>
                      <SelectItem value="manual">Manual / Bank Transfer</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <div className="space-y-2"><Label>Notes</Label><Textarea value={createForm.notes} onChange={(e) => setCreateForm({ ...createForm, notes: e.target.value })} placeholder="Optional notes..." rows={2} /></div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button onClick={handleCreate} className="bg-sag-navy hover:bg-sag-navy-800" data-testid="submit-create-invoice">Create Invoice</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Mark as Paid Dialog */}
        <Dialog open={markPaidOpen} onOpenChange={setMarkPaidOpen}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Building2 className="h-5 w-5 text-emerald-600" />
                Mark as Paid (Bank Transfer)
              </DialogTitle>
            </DialogHeader>
            {selectedInvoice && (
              <div className="space-y-4">
                <div className="bg-slate-50 rounded-lg p-3">
                  <p className="text-sm"><strong>{selectedInvoice.invoice_number}</strong> &middot; {selectedInvoice.client_name}</p>
                  <p className="text-xl font-bold text-sag-navy mt-1">${selectedInvoice.total.toLocaleString()} {selectedInvoice.currency}</p>
                </div>
                <div className="space-y-2">
                  <Label>Reference Number *</Label>
                  <Input value={markPaidForm.reference_number} onChange={(e) => setMarkPaidForm({ ...markPaidForm, reference_number: e.target.value })} placeholder="e.g., BT-2026-0315-001" data-testid="mark-paid-reference" />
                </div>
                <div className="space-y-2">
                  <Label>Bank Name *</Label>
                  <Input value={markPaidForm.bank_name} onChange={(e) => setMarkPaidForm({ ...markPaidForm, bank_name: e.target.value })} placeholder="e.g., Scotiabank Bahamas" data-testid="mark-paid-bank" />
                </div>
                <div className="space-y-2">
                  <Label>Date Received *</Label>
                  <Input type="date" value={markPaidForm.date_received} onChange={(e) => setMarkPaidForm({ ...markPaidForm, date_received: e.target.value })} data-testid="mark-paid-date" />
                </div>
                <div className="space-y-2">
                  <Label>Notes</Label>
                  <Textarea value={markPaidForm.notes} onChange={(e) => setMarkPaidForm({ ...markPaidForm, notes: e.target.value })} placeholder="Additional details..." rows={2} />
                </div>
              </div>
            )}
            <DialogFooter>
              <Button variant="outline" onClick={() => setMarkPaidOpen(false)}>Cancel</Button>
              <Button onClick={handleMarkPaid} className="bg-emerald-600 hover:bg-emerald-700" data-testid="confirm-mark-paid">Confirm Payment</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Invoice Detail Dialog */}
        <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader><DialogTitle>Invoice Detail</DialogTitle></DialogHeader>
            {detailInvoice && (
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <p className="font-mono text-lg font-bold">{detailInvoice.invoice_number}</p>
                  <Badge className={statusColors[detailInvoice.status]}><span className="capitalize">{detailInvoice.status}</span></Badge>
                </div>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div><p className="text-slate-500">Client</p><p className="font-medium">{detailInvoice.client_name}</p></div>
                  <div><p className="text-slate-500">Amount</p><p className="font-bold text-lg">${detailInvoice.total.toLocaleString()} {detailInvoice.currency}</p></div>
                  <div><p className="text-slate-500">Billing Type</p><p className="font-medium capitalize">{detailInvoice.billing_type}</p></div>
                  <div><p className="text-slate-500">Due Date</p><p className="font-medium">{new Date(detailInvoice.due_date).toLocaleDateString()}</p></div>
                  {detailInvoice.billing_period_start && <div><p className="text-slate-500">Period</p><p className="font-medium">{detailInvoice.billing_period_start} — {detailInvoice.billing_period_end}</p></div>}
                  <div><p className="text-slate-500">Payment Method</p><p className="font-medium capitalize">{(detailInvoice.payment_method || "—").replace("_", " ")}</p></div>
                </div>
                {/* Items */}
                <div>
                  <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Line Items</p>
                  {detailInvoice.items.map((item, i) => (
                    <div key={`item-${item.description}-${i}`} className="flex items-center justify-between py-2 border-b border-slate-100">
                      <div><p className="text-sm font-medium">{item.description}</p><p className="text-xs text-slate-500">{item.quantity} x ${item.unit_price}</p></div>
                      <p className="font-semibold">${item.total.toLocaleString()}</p>
                    </div>
                  ))}
                </div>
                {/* Manual Payment Info */}
                {detailInvoice.manual_payment && (
                  <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-3">
                    <p className="text-xs text-emerald-600 uppercase tracking-wide font-medium mb-2">Payment Confirmation</p>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div><p className="text-slate-500">Reference</p><p className="font-medium">{detailInvoice.manual_payment.reference_number}</p></div>
                      <div><p className="text-slate-500">Bank</p><p className="font-medium">{detailInvoice.manual_payment.bank_name}</p></div>
                      <div><p className="text-slate-500">Date Received</p><p className="font-medium">{detailInvoice.manual_payment.date_received}</p></div>
                      <div><p className="text-slate-500">Confirmed By</p><p className="font-medium">{detailInvoice.manual_payment.confirmed_by_name}</p></div>
                    </div>
                    {detailInvoice.manual_payment.notes && <p className="text-sm text-slate-600 mt-2">{detailInvoice.manual_payment.notes}</p>}
                  </div>
                )}
                {/* Audit Trail */}
                {detailInvoice.audit_log?.length > 0 && (
                  <div>
                    <p className="text-xs text-slate-500 uppercase tracking-wide mb-2">Audit Trail</p>
                    <div className="space-y-2 max-h-60 overflow-y-auto">
                      {detailInvoice.audit_log.map((entry, i) => {
                        const isEmail = entry.action === "invoice_emailed" || entry.action === "reminder_sent";
                        const isRef = entry.action === "deposit_confirmation_uploaded";
                        return (
                          <div key={`audit-${entry.timestamp}-${i}`} className={`flex items-start gap-2 text-xs p-2 rounded ${isEmail ? "bg-blue-50" : isRef ? "bg-amber-50" : ""}`}>
                            <div className={`w-1.5 h-1.5 rounded-full mt-1.5 flex-shrink-0 ${isEmail ? "bg-blue-500" : isRef ? "bg-amber-500" : "bg-slate-400"}`} />
                            <div className="flex-1">
                              <p className="text-slate-700">
                                <strong>{entry.user_name}</strong>
                                {isEmail && <span className="ml-1 text-blue-600">[Email]</span>}
                                {isRef && <span className="ml-1 text-amber-600">[Payment Ref]</span>}
                                : {entry.details}
                              </p>
                              <p className="text-slate-400">{new Date(entry.timestamp).toLocaleString()}</p>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
}
